'use client';

/**
 * ManageConnectionModal Component
 *
 * Modal for managing an existing connection - linking/unlinking projects
 * and disconnecting the connection.
 *
 * @example
 * ```tsx
 * <ManageConnectionModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   connection={selectedConnection}
 *   organizationId="org-123"
 *   onUpdate={() => fetchConnections()}
 * />
 * ```
 */

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  Center,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Inline,
  Label,
  Spinner,
  Stack,
  Text,
} from '@cortex/ui';
import { AlertTriangle, Github, User } from 'lucide-react';
import * as React from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';

import { ConnectionHealthBadge } from './ConnectionHealthBadge';

interface ManageConnectionModalProps {
  /**
   * Whether the modal is open
   */
  open: boolean;

  /**
   * Callback when modal should close
   */
  onClose: () => void;

  /**
   * Connection to manage (null if none selected)
   */
  connection: {
    id: string;
    label: string;
    providerId: string;
    providerType: string;
    metadata: {
      github_user?: {
        login: string;
        avatar_url: string;
      };
      expires_at?: string;
      refresh_expires_at?: string;
    };
    validationStatus: string | null;
    linkedProjectIds?: string[];
  } | null;

  /**
   * Organization ID
   */
  organizationId: string;

  /**
   * Callback when connection is updated
   */
  onUpdate?: () => void;
}

type Project = {
  id: string;
  name: string;
  slug: string;
};

/**
 * Get provider icon based on provider ID
 */
function getProviderIcon(providerId: string) {
  switch (providerId.toLowerCase()) {
    case 'github':
      return <Github className="h-5 w-5" />;
    default:
      return null;
  }
}

export function ManageConnectionModal({
  open,
  onClose,
  connection,
  organizationId,
  onUpdate,
}: ManageConnectionModalProps) {
  const fetchWithAuth = useFetchWithAuth();
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [linkedProjectIds, setLinkedProjectIds] = React.useState<Set<string>>(
    new Set()
  );
  const [loading, setLoading] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] =
    React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Fetch projects callback
  const fetchProjects = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(
        `/admin/organizations/${organizationId}/projects`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      const { data } = await response.json();
      setProjects(data.items || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [organizationId, fetchWithAuth]);

  // Fetch projects when modal opens
  React.useEffect(() => {
    if (open && organizationId) {
      fetchProjects();
    }
  }, [open, organizationId, fetchProjects]);

  // Initialize linked project IDs
  React.useEffect(() => {
    if (connection?.linkedProjectIds) {
      setLinkedProjectIds(new Set(connection.linkedProjectIds));
    }
  }, [connection]);

  const handleToggleProject = async (projectId: string) => {
    if (!connection) return;

    const isLinked = linkedProjectIds.has(projectId);
    setUpdating(true);
    setError(null);

    try {
      if (isLinked) {
        // Unlink
        const response = await fetchWithAuth(
          `/admin/connections/${connection.id}/projects/${projectId}`,
          { method: 'DELETE' }
        );

        if (!response.ok) {
          throw new Error('Failed to unlink project');
        }

        setLinkedProjectIds((prev) => {
          const next = new Set(prev);
          next.delete(projectId);
          return next;
        });
      } else {
        // Link
        const response = await fetchWithAuth(
          `/admin/connections/${connection.id}/projects`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ projectId }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to link project');
        }

        setLinkedProjectIds((prev) => new Set([...prev, projectId]));
      }

      onUpdate?.();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to ${isLinked ? 'unlink' : 'link'} project`
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleDisconnect = async () => {
    if (!connection) return;

    setDisconnecting(true);
    setError(null);

    try {
      const response = await fetchWithAuth(
        `/admin/connections/${connection.id}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok && response.status !== 204) {
        throw new Error('Failed to disconnect');
      }

      onUpdate?.();
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to disconnect connection'
      );
    } finally {
      setDisconnecting(false);
    }
  };

  if (!connection) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Manage Connection</DialogTitle>
          <DialogDescription>
            Link projects and manage connection settings
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Stack gap="lg">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Connection Info */}
            <Card className="bg-surface-2 p-4">
              <Inline align="start" justify="between">
                <Inline gap="sm">
                  <Center className="bg-surface-3 h-10 w-10 rounded-full">
                    {getProviderIcon(connection.providerId)}
                  </Center>
                  <Stack gap="none">
                    <Text weight="semibold">{connection.label}</Text>
                    <Text size="sm" color="muted">
                      {connection.providerId}
                    </Text>
                    {connection.metadata.github_user && (
                      <Inline gap="xs" className="mt-1">
                        <Avatar className="h-4 w-4">
                          <AvatarImage
                            src={connection.metadata.github_user.avatar_url}
                            alt={connection.metadata.github_user.login}
                          />
                          <AvatarFallback>
                            <User className="h-2.5 w-2.5" />
                          </AvatarFallback>
                        </Avatar>
                        <Text size="sm" color="muted">
                          @{connection.metadata.github_user.login}
                        </Text>
                      </Inline>
                    )}
                  </Stack>
                </Inline>
                <ConnectionHealthBadge
                  expiresAt={connection.metadata.expires_at ?? undefined}
                  refreshExpiresAt={
                    connection.metadata.refresh_expires_at ?? undefined
                  }
                  validationStatus={connection.validationStatus}
                />
              </Inline>
            </Card>

            {/* Linked Projects */}
            <Stack gap="sm">
              <Text weight="semibold">Linked Projects</Text>
              {loading ? (
                <Center minHeight="8rem">
                  <Spinner size="md" variant="muted" />
                </Center>
              ) : projects.length === 0 ? (
                <Text color="muted" size="sm">
                  No projects found in this organization
                </Text>
              ) : (
                <Stack gap="xs">
                  {projects.map((project) => {
                    const isLinked = linkedProjectIds.has(project.id);
                    return (
                      <Card
                        key={project.id}
                        className="hover:bg-surface-2 p-3 transition-colors"
                      >
                        <Label className="flex cursor-pointer items-center gap-3">
                          <Checkbox
                            checked={isLinked}
                            onCheckedChange={() =>
                              handleToggleProject(project.id)
                            }
                            disabled={updating}
                          />
                          <Stack className="flex-1" gap="none">
                            <Text weight="medium" size="sm">
                              {project.name}
                            </Text>
                            <Text size="xs" color="muted">
                              {project.slug}
                            </Text>
                          </Stack>
                        </Label>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </Stack>

            {/* Danger Zone */}
            <Alert variant="destructive">
              <AlertTriangle className="h-5 w-5" />
              <AlertTitle>Danger Zone</AlertTitle>
              <AlertDescription>
                <Text size="sm" color="muted" className="mb-3">
                  Disconnecting will remove this connection and unlink all
                  projects. This action cannot be undone.
                </Text>
                {!showDisconnectConfirm ? (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowDisconnectConfirm(true)}
                  >
                    Disconnect
                  </Button>
                ) : (
                  <Inline gap="xs">
                    <Text size="sm" weight="medium" color="error">
                      Are you sure?
                    </Text>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDisconnect}
                      loading={disconnecting}
                    >
                      Yes, Disconnect
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDisconnectConfirm(false)}
                    >
                      Cancel
                    </Button>
                  </Inline>
                )}
              </AlertDescription>
            </Alert>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
